import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { buildUpiCatalog, enrichUpiApp } from '../utils/upiIcons';
import { isTossitNativeApp } from '../utils/nativeBridge';
import upiCatalogData from '../data/upiApps.json';

// Holds the list of UPI apps the NATIVE host says are installed/usable. The native side
// pushes these by calling the global window.tossitSetUpiCapabilities(apps) — possibly
// multiple times over the page's life (dashboard load, checkout mount, app foreground).
// We ALWAYS replace (never merge). In a plain browser (or an app build before the native
// UPI bridge ships) this is never called, so capabilities stay [] — that IS the normal
// default, not an error. UI shows native UPI buttons purely by feature-detection.
// apps: [{ id: string(package), displayName: string, icon?: string(base64) }, ...]
//
// The bundled icon/label catalog (src/data/upiApps.json, ~3KB) is imported STATICALLY and
// its lookup map built ONCE at module load, so the default/preferred UPI app icon is
// enriched on the very first render — no lazy chunk fetch, no generic-badge flash before
// the branded icon appears.

const UPI_CATALOG = buildUpiCatalog(upiCatalogData);

const UpiCapabilitiesContext = createContext({ upiApps: [], isOldNativeApp: false });

export const UpiCapabilitiesProvider = ({ children }) => {
    const [rawApps, setRawApps] = useState([]);
    // Old native apps never call tossitSetUpiCapabilities; new ones do (on load/foreground).
    // In the webview, if 10s pass with no call, we treat the app as an OLD version so the UI
    // can prompt the user to update (UPI unavailable). A later call clears the flag.
    const [bridgeCalled, setBridgeCalled] = useState(false);
    const [staleChecked, setStaleChecked] = useState(false);

    useEffect(() => {
        window.tossitSetUpiCapabilities = (apps) => {
            setBridgeCalled(true);
            setRawApps(Array.isArray(apps) ? apps : []);
        };
        let timer = null;
        if (isTossitNativeApp()) {
            timer = setTimeout(() => setStaleChecked(true), 10000);
        }
        return () => {
            if (timer) clearTimeout(timer);
            try { delete window.tossitSetUpiCapabilities; } catch (e) { /* noop */ }
        };
    }, []);

    const upiApps = useMemo(
        () => rawApps.map((a) => enrichUpiApp(a, UPI_CATALOG)),
        [rawApps]
    );

    // Webview only: detected old app = in native shell, 10s elapsed, and no capabilities push.
    const isOldNativeApp = isTossitNativeApp() && staleChecked && !bridgeCalled;

    return (
        <UpiCapabilitiesContext.Provider value={{ upiApps, isOldNativeApp }}>
            {children}
        </UpiCapabilitiesContext.Provider>
    );
};

export const useUpiCapabilities = () => useContext(UpiCapabilitiesContext);
